import {
    FormControl, FormErrorMessage, FormHelperText,
    FormLabel, Input as ChakraInput, InputGroup, InputRightElement,
    InputLeftElement, useMultiStyleConfig
} from '@chakra-ui/react';
import type {
    FormControlProps, InputProps
} from '@chakra-ui/react';
import React, {
    ChangeEvent, forwardRef, useState
} from 'react';

import { SuccessIcon } from './components/success-icon';
import { UserIssueIcon } from './components/user-issue-icon';
import { WarningIcon } from './components/warning-icon';

export type TextInputControlProps =
    & Omit<FormControlProps, 'defaultValue'>
    & {
    /**
     * The unique id given to the control for usage in the form.
     */
    id: string
    /**
     * Helper attribute convention for testing libraries.
     */
    'data-testid'?: string
    /**
     * The text for the label on top of the input, used to describe the purpose of the input.
     */
    labelText?: string
    /**
     * Text for showing the user more descriptive content than the label,
     * eg error message text and ux suggestions on about the data to input.
     */
    helperText?: string
    /**
     * The Chakra <Input/> element props to pass down through this control.
     */
    inputProps?: InputProps
    /**
     * Override the default behaviour for determining if the input has any content,
     * this is used for the material ui UX with the label animation.
     */
    hasValuePredicate?: (value: unknown) => boolean
    /**
     * A warning state to show the user something requires attention.
     */
    isWarning?: boolean
    /**
     * A warning state to show the user something requires attention that has to do with a user,
     * a user like icon shows in this state.
     */
    isUserWarning?: boolean
    /**
     * A state to show the user that something has worked correctly.
     */
    isSuccess?: boolean
    /**
     * A React element to render to the right and inside the input, eg an icon or visual aid.
     */
    RightAdornment?: React.ReactElement | null
    /**
     * A React element to render to the right and inside the input, eg an icon or visual aid.
     */
    LeftAdornment?: React.ReactElement | null
};

/**
 * A Form Control component to handle user text input in a form.
 * It is designed from the Material UI design and UX with tweaks from 3p UX team.
 *
 * The React ref is passed through to the input element for usage in scenarios like Chakra Popup.
 *
 * @see the story 'Components/TextInputControl' for states and features.
 */
export const TextInputControl = forwardRef<HTMLInputElement, TextInputControlProps>(
    ({
        id,
        labelText = null,
        helperText = null,
        inputProps = {},
        hasValuePredicate = hasVisibleValuePredicate,
        isSuccess = false,
        isUserWarning = false,
        isWarning = false,
        RightAdornment: RightAdornmentProp = null,
        LeftAdornment = null,
        ...restProps
    }, ref) => {
        const [isFocused, setIsFocused] = useState<boolean>(false);
        const [hasValue, setHasValue] = useState<boolean>(
            hasValuePredicate(inputProps?.value || inputProps?.defaultValue || '')
        );
        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            setHasValue(hasValuePredicate(event.target.value));
            restProps.onChange && restProps.onChange(event);
        };
        const styles = useMultiStyleConfig('TextInputControl', {
            isFocused,
            hasValue,
            isInvalid: restProps.isInvalid,
            isReadOnly: restProps.isReadOnly,
            isDisabled: restProps.isDisabled,
            isWarning,
            isUserWarning,
            isSuccess
        });

        let RightAdornment: any = RightAdornmentProp;

        if (isWarning && !isFocused) {
            RightAdornment = <WarningIcon />;
        }

        if (isUserWarning && !isFocused) {
            RightAdornment = <UserIssueIcon />;
        }

        if (isSuccess && !isFocused) {
            RightAdornment = <SuccessIcon />;
        }

        return (
            <FormControl
                {...restProps}
                data-has-value={hasValue}
                position={'relative'}
                sx={styles.FormControl}
                onFocus={(event) => {
                    setIsFocused(true);
                    restProps.onFocus && restProps.onFocus(event);
                }}
                onBlur={(event) => {
                    setIsFocused(false);
                    restProps.onBlur && restProps.onBlur(event);
                }}
                onChange={handleChange}
            >
                {labelText ?
                    <FormLabel
                        htmlFor={id}
                        sx={styles.FormLabel}
                    >
                        {labelText}
                    </FormLabel> : null}

                <InputGroup>
                    {LeftAdornment ?
                        <InputLeftElement
                            sx={styles.InputLeftElement}
                            children={LeftAdornment}
                        /> : null}

                    <ChakraInput
                        id={id}
                        sx={styles.Input}
                        autoComplete={'off'}
                        ref={ref}
                        {...inputProps}
                    />

                    {RightAdornment ?
                        <InputRightElement
                            sx={styles.InputRightElement}
                            children={RightAdornment}
                        /> : null}
                </InputGroup>

                {helperText ?
                    restProps.isInvalid ?
                        <FormErrorMessage
                            data-testid={`helper-text-${id}`}
                            data-label-type={'error'}
                            sx={styles.FormErrorMessage}
                        >
                            {helperText}
                        </FormErrorMessage> :
                        <FormHelperText
                            data-testid={`helper-text-${id}`}
                            data-label-type={'helper'}
                            sx={styles.FormHelperText}
                        >
                            {helperText}
                        </FormHelperText> : null
                }
            </FormControl>
        );
    }
);

/**
 * As per material design determine if there is a valid value
 * for the UX to adapt the design.
 */
function hasVisibleValuePredicate (value: unknown): boolean {
    if (typeof value !== 'string') {
        return false;
    }

    return value.length > 0;
}
